"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useTheme } from "next-themes";
import { brandingApi } from "@/lib/api";
import type { PublicBranding } from "@/lib/types";

/** The default product brand used whenever no agency branding applies. */
export const DEFAULT_BRAND_NAME = "Accio SMM";

interface BrandingContextValue {
  /** The resolved agency branding, or null when the default applies. */
  branding: PublicBranding | null;
  /** Brand name to render (resolved brand, or the default product name). */
  brandName: string;
  /** Logo URL to render, or null when none is configured. */
  logoUrl: string | null;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  brandName: DEFAULT_BRAND_NAME,
  logoUrl: null,
});

// ─── Palette helpers ────────────────────────────────────────────────────────

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const isHex = (s?: string | null): s is string => !!s && HEX_RE.test(s.trim());

function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.trim().replace(/^#/, "");
  const full =
    m.length === 3
      ? m
          .split("")
          .map((c) => c + c)
          .join("")
      : m;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(rgb: number[]): string {
  return (
    "#" +
    rgb
      .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
      .join("")
  );
}

/** WCAG relative luminance (0 = black, 1 = white). */
function luminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Black or white text, whichever is more readable on `bg` — contrast safety. */
function readableText(bg: string): string {
  return luminance(bg) > 0.5 ? "#141414" : "#ffffff";
}

/** Linear blend of two hex colors (t=0 → a, t=1 → b). */
function mix(a: string, b: string, t: number): string {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return a;
  return toHex([0, 1, 2].map((i) => ra[i] * (1 - t) + rb[i] * t));
}

/**
 * Applies a white-label palette to the theme by overriding CSS variables on the
 * document root, returning a cleanup that restores the prior values.
 *
 * Contrast safety: every *-foreground token is DERIVED (black/white by
 * luminance) rather than admin-set, so text stays readable on any brand color.
 *
 * Dark-mode: the brand primary/accent apply in both modes, but the
 * background/surface/text palette is applied in LIGHT mode only — dark mode keeps
 * the app's tuned neutrals so an admin can't produce an unreadable dark theme.
 */
function applyPalette(b: PublicBranding | null, isDark: boolean): () => void {
  if (typeof document === "undefined" || !b) return () => {};
  const root = document.documentElement;
  const vars: Record<string, string> = {};

  // Brand accent — both light and dark.
  if (isHex(b.primary_color)) {
    vars["--primary"] = b.primary_color;
    vars["--ring"] = b.primary_color;
    vars["--primary-foreground"] = readableText(b.primary_color);
  }
  if (isHex(b.secondary_color)) {
    vars["--accent-foreground"] = b.secondary_color;
  }

  // Surface / background / text — light mode only.
  if (!isDark) {
    const bg = isHex(b.background_color) ? b.background_color : null;
    const fg = isHex(b.foreground_color) ? b.foreground_color : null;
    const surface = isHex(b.surface_color) ? b.surface_color : bg;

    if (bg) vars["--background"] = bg;
    if (fg) vars["--foreground"] = fg;
    if (surface) {
      vars["--card"] = surface;
      vars["--popover"] = surface;
    }
    if (fg) {
      vars["--card-foreground"] = fg;
      vars["--popover-foreground"] = fg;
    }
    if (bg && fg) {
      vars["--muted"] = mix(surface ?? bg, fg, 0.05);
      vars["--muted-foreground"] = mix(fg, bg, 0.42);
      vars["--border"] = mix(fg, bg, 0.86);
      vars["--secondary"] = mix(bg, fg, 0.05);
      vars["--secondary-foreground"] = fg;
    }
    if (isHex(b.secondary_color) && bg) {
      vars["--accent"] = mix(bg, b.secondary_color, 0.14);
    }
  }

  const prev: Record<string, string> = {};
  for (const k of Object.keys(vars)) {
    prev[k] = root.style.getPropertyValue(k);
    root.style.setProperty(k, vars[k]);
  }
  return () => {
    for (const k of Object.keys(vars)) {
      if (prev[k]) root.style.setProperty(k, prev[k]);
      else root.style.removeProperty(k);
    }
  };
}

/**
 * Swaps the browser-tab favicon to `url` by APPENDING our own <link rel="icon">.
 *
 * We deliberately do NOT remove Next's own icon links (app/favicon.ico,
 * app/icon.png, app/apple-icon.png): under Next 16 / React 19 those links are
 * hoisted and owned by React, and detaching them makes React crash on
 * removeChild during reconciliation. Instead we let ours WIN by hinting it as a
 * scalable icon — `sizes="any"` plus an `image/svg+xml` type make Chromium/Firefox
 * prefer it over the fixed-size raster defaults. The favicon url carries a `.svg`
 * extension (ServeAsset ignores it) so the type can be inferred. Only http(s)
 * urls and hosted branding assets are honored.
 */
function applyFavicon(url: string | undefined | null): () => void {
  if (typeof document === "undefined") return () => {};
  const href = (url ?? "").trim();
  if (!/^(https?:\/\/|\/api\/v1\/branding\/assets\/)/i.test(href)) return () => {};

  const link = document.createElement("link");
  link.rel = "icon";
  link.setAttribute("sizes", "any");
  if (/\.svg(\?|$)/i.test(href)) link.type = "image/svg+xml";
  link.href = href;
  link.setAttribute("data-branding-favicon", "");
  document.head.appendChild(link);
  return () => {
    link.remove();
  };
}

/**
 * BrandingProvider fetches the effective branding for the signed-in caller
 * (GET /api/v1/branding/me — own agency, or a client's parent agency) once on
 * mount, applies the agency primary color to the theme, and exposes the brand
 * name + logo to the sidebar. It is rendered inside the authenticated layout so
 * the cookie session is present; a missing/empty branding is the default state
 * and is fully reversible.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PublicBranding | null>(null);

  useEffect(() => {
    let active = true;
    void brandingApi
      .me()
      .then((res) => {
        if (active) setBranding(res.branding);
      })
      .catch(() => {
        // No branding / not reachable → keep the default brand silently.
      });
    return () => {
      active = false;
    };
  }, []);

  // Apply (and clean up) the full palette whenever the branding OR the
  // light/dark mode changes (dark keeps the app neutrals, so we must re-apply on
  // a theme switch).
  const { resolvedTheme } = useTheme();
  useEffect(
    () => applyPalette(branding, resolvedTheme === "dark"),
    [branding, resolvedTheme],
  );

  // Swap the browser-tab favicon to the tenant's, restoring the default on
  // cleanup. Only http(s) urls are honored (block data:/other schemes).
  useEffect(() => applyFavicon(branding?.favicon_url), [branding]);

  const brandName =
    branding?.brand_name && branding.brand_name.trim()
      ? branding.brand_name
      : DEFAULT_BRAND_NAME;
  // Only surface http(s) logo URLs — the white-label value is tenant-set, so
  // block javascript:/data:/other schemes before it ever reaches an <img src>.
  // Prefer the dark-theme logo in dark mode when one is set; otherwise fall back
  // to the single light logo for both themes.
  const isDark = resolvedTheme === "dark";
  const safeLogo = (v: string | undefined): string | null => {
    const t = v?.trim();
    return t && /^(https?:\/\/|\/api\/v1\/branding\/assets\/)/i.test(t)
      ? t
      : null;
  };
  const lightLogo = safeLogo(branding?.logo_url);
  const darkLogo = safeLogo(branding?.logo_dark_url);
  const logoUrl = (isDark ? darkLogo ?? lightLogo : lightLogo) ?? null;

  return (
    <BrandingContext.Provider value={{ branding, brandName, logoUrl }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

/**
 * Sizing for a white-label logo, wherever one is rendered.
 *
 * Constrained on BOTH axes, because a client's logo can be any shape and the
 * previous rules each assumed one:
 *
 *   - the header locked height (`h-11`), so a WIDE lockup filled the space but
 *     a SQUARE mark rendered 44x44 and looked lost in an 80px bar;
 *   - the login card used a fixed square (`size-12`), which did the opposite —
 *     fine for a square mark, and a 48x12 sliver for a wide one.
 *
 * With a max on each axis and `object-contain`, whichever dimension binds
 * first wins and any aspect ratio fills the space it is given.
 */
export const BRAND_LOGO_CLASS = "h-auto max-h-14 w-auto max-w-[200px] object-contain";
